import { Controller } from "@hotwired/stimulus"
import { loadMembers, filterMembers, renderMemberRow, findMentionQuery, mentionPopupVisible } from "../lib/mention_suggestion"
import { createSuggestionRenderer } from "../lib/suggestion_popup"

// FD-622: the "@" mention picker for a PLAIN <input>/<textarea>.
//
// FD-621 built this for the Tiptap block editor on @tiptap/suggestion. The asks table's
// "Blocker / notes" field is a bare textarea in a dense inline-edit table (FD-614 budgeted
// that table down to ~139px of overflow at 1400px, and FD-615 is adding a column), so a
// per-row rich editor was not an option — but a second picker implementation would be two
// search behaviours and two member fetches to keep in sync. So this owns only the parts that
// are genuinely different — caret/range arithmetic on a string value, and key handling that
// ProseMirror's keymap used to do — and imports everything else:
//
//   loadMembers / filterMembers / renderMemberRow  <- lib/mention_suggestion (FD-621)
//   createSuggestionRenderer                       <- lib/suggestion_popup (FD-209/FD-621)
//
// loadMembers is cached per URL, so an asks table with 30 rows makes ONE request between
// them, and it is shared with any block editor on the same page.
//
// OPT-IN, like the editor's. MentionScanJob only scans WorkItem/Page/WikiPage/Comment and
// (FD-622) MeetingAsk — offering the picker anywhere else would promise a notification that
// nothing will ever send.
export default class extends Controller {
  static values = { url: String }

  connect() {
    // Listeners are bound here rather than through data-action because this needs five of
    // them and the element already carries `change->auto-submit#submit`; merging six entries
    // into one attribute is far harder to read than this.
    // Prefetch, and reset the per-focus memo so a lookup that failed earlier gets one more
    // chance — see members().
    this.onFocus = () => { this.membersPromise = null; this.members() }
    this.onInput = () => this.refresh()
    this.onClick = () => this.refresh()
    this.onKeyUp = (event) => this.handleKeyUp(event)
    this.onKeyDown = (event) => this.handleKeyDown(event)
    this.onBlur = () => this.close()

    this.element.addEventListener("focus", this.onFocus)
    this.element.addEventListener("input", this.onInput)
    this.element.addEventListener("click", this.onClick)
    this.element.addEventListener("keyup", this.onKeyUp)
    this.element.addEventListener("keydown", this.onKeyDown)
    this.element.addEventListener("blur", this.onBlur)
  }

  disconnect() {
    this.element.removeEventListener("focus", this.onFocus)
    this.element.removeEventListener("input", this.onInput)
    this.element.removeEventListener("click", this.onClick)
    this.element.removeEventListener("keyup", this.onKeyUp)
    this.element.removeEventListener("keydown", this.onKeyDown)
    this.element.removeEventListener("blur", this.onBlur)
    this.close()
  }

  // ONE lookup per focus, not one per keystroke. loadMembers caches by URL but deliberately
  // EVICTS a failed fetch so the outage stays transient — which, called straight from
  // refresh(), means every keystroke re-fires the request. One 429 (the endpoint is throttled)
  // or one expired session then sustains itself, and each failure renders as "No matching
  // people", which reads as fact rather than breakage. Memoising per focus bounds it: the
  // retry happens when the user comes back to the field.
  members() {
    return (this.membersPromise ||= loadMembers(this.urlValue))
  }

  // --- query detection -------------------------------------------------------------

  // The plain-string twin of the block editor's findSuggestionMatch, and deliberately the
  // SAME rule rather than a second regex: findMentionQuery is the one definition of what is
  // being typed after an "@" (FD-904). The query runs from an "@" that starts a word to the
  // caret and may carry up to two internal spaces, so "@Brad Sch" keeps the picker open on a
  // multi-word name; a third space, a double space or a second "@" end it, which is also why
  // "brad@example.com" opens nothing. MeetingAsk#mentionable_text strips addresses
  // server-side for the same reason; this is the client half of that agreement.
  activeQuery() {
    const caret = this.element.selectionStart
    if (caret === null || caret !== this.element.selectionEnd) return null
    const found = findMentionQuery(this.element.value.slice(0, caret))
    if (!found) return null
    return { query: found.query, at: found.at, caret }
  }

  // The ONLY way to ask "is the menu open?". A Turbo morph (any row's auto-submit
  // redirect_backs to the same URL) deletes the body-mounted popup while this controller and
  // its input survive in place — so a stored boolean would say "open" about a menu the user
  // watched vanish, and refresh() would never rebuild it. The picker went silently dead for
  // the rest of the field. Liveness is the shell's to know, so ask it, and drop the corpse.
  activeRenderer() {
    if (this.renderer && !this.renderer.isLive()) this.close()
    return this.renderer
  }

  refresh() {
    const active = this.activeQuery()
    // No query left to dismiss, so forget the dismissal too — the next "@" starts clean. The
    // editor twin does exactly this (`if (!match) next.dismissedRange = null`).
    if (!active) { this.dismissedAt = null; return this.close() }
    this.range = active

    // FD-904: Escape stays honoured. A space is an ordinary keystroke inside a query now, so
    // without this memory the next space after an Escape re-opened the picker on the very
    // mention the user had just dismissed. Keyed on the "@" offset, which is the plain-string
    // equivalent of the plugin's dismissedRange.from: it dismisses the mention being typed,
    // not the field, so a new "@" anywhere else still opens.
    if (this.dismissedAt === active.at) return this.close()

    // FD-904: the text an insert just left behind is itself a valid query — "@River Song "
    // still matches River Song — and insert() ends by dispatching `input`, which lands right
    // here. Skip exactly the query the insert completed, and re-arm the moment it changes.
    // (The editor twin gets this from the library's dismissedRange, via exitSuggestion.)
    if (this.completed && active.query === this.completed) return this.close()
    this.completed = null

    // FD-904: a query that already carries a space is prose until somebody matches it, so it
    // never opens the popup speculatively — "@Brad said hi" must not park "No matching
    // people" over what is being written. A query with no space opens it immediately, as
    // before: loading:true with an empty list makes the shell render its loading row and
    // swallow the navigation keys, instead of claiming "no matches" and letting Enter through
    // while the very first fetch is still in flight.
    if (!this.activeRenderer() && mentionPopupVisible(active.query, [])) this.open([], true)

    // A generation counter, because members arrive asynchronously: without it a resolve
    // from two keystrokes ago repaints the popup with a stale list, or reopens one the user
    // has already dismissed. Counted AFTER the open above, because activeRenderer() drops a
    // morph-killed popup through close(), which bumps this very counter — captured any
    // earlier, every resolve that followed a recovery would read as stale and the fresh
    // popup would sit on "Loading…" for a keystroke.
    const generation = (this.generation = (this.generation || 0) + 1)
    const stale = () => generation !== this.generation

    this.members().then((members) => {
      if (stale()) return
      const items = filterMembers(members, active.query)
      // Spaced and matching nobody: hide, rather than sitting on the empty state until the
      // third space ends the query. An unspaced no-match still shows it (FD-622: a visible
      // popup owns Enter, so "@zzzz" then Enter can't submit the form behind it).
      if (!mentionPopupVisible(active.query, items)) return this.close()
      if (this.activeRenderer()) this.renderer.onUpdate(this.props(items, false))
      else this.open(items, false)
    })
  }

  open(items, loading) {
    this.renderer = createSuggestionRenderer({
      prefix: "fd-mention",
      emptyText: "No matching people",
      renderItem: renderMemberRow,
    })
    this.renderer.onStart(this.props(items, loading))
  }

  props(items, loading) {
    return {
      items,
      loading,
      command: (member) => this.insert(member),
      // `element` is what the shell mounts against — it resolves the nearest OPEN <dialog>
      // from here, so the picker works inside a modal without being clipped by the asks
      // card's own overflow container.
      element: this.element,
      // Anchored to the whole field rather than the caret. A textarea gives no caret rect
      // without mirroring its content into a hidden div, and this cell is one or two lines
      // tall — the shell already flips the popup above when it would overflow the viewport.
      clientRect: () => this.element.getBoundingClientRect(),
    }
  }

  // Close AND remember, for as long as the same "@" is being typed. See refresh().
  dismiss() {
    const active = this.activeQuery()
    this.dismissedAt = active ? active.at : null
    this.close()
  }

  close() {
    // The bump comes FIRST and happens even with no popup open. A spaced query holds an
    // in-flight members() resolve while showing NOTHING (it earns the popup only once someone
    // matches), so an early return here left that resolve armed: blur, disconnect, or the
    // query being deleted would then be followed by a popup opening over a field the user had
    // already left. Orphaning is the only thing that can stop it — the resolve doesn't
    // re-read the field.
    this.generation = (this.generation || 0) + 1 // orphan any in-flight resolve
    if (!this.renderer) return
    this.renderer.onExit()
    this.renderer = null
  }

  // --- insertion -------------------------------------------------------------------

  insert(member) {
    // Re-derive the splice point from the LIVE value rather than trusting the range captured
    // when the popup was painted. A same-URL Turbo morph can rewrite this field's value and
    // caret between those two moments, and splicing at stale offsets would corrupt whatever
    // the morph put there. Nothing to replace means the mention is no longer being typed.
    const active = this.activeQuery()
    if (!active) return this.close()
    const { at, caret } = active
    const value = this.element.value
    const token = `@${member.token} `
    this.element.value = value.slice(0, at) + token + value.slice(caret)
    const cursor = at + token.length
    this.element.setSelectionRange(cursor, cursor)
    this.completed = `${member.token} ` // see refresh(): don't re-offer what was just picked
    this.close()
    this.element.focus({ preventScroll: true }) // FD-1190: the field being typed in is on screen

    // NO synthetic `change`. The cell auto-submits on change (auto_submit_controller), which
    // redirect_backs to the same URL — a Turbo morph that would delete this popup out of
    // <body> mid-gesture and rewrite the field's value and caret from the server render.
    // Setting .value programmatically fires nothing, so the row persists on blur exactly as
    // it does when the name is typed by hand. `input` IS dispatched, so field-sizing-content
    // and any other input listener still see the change.
    this.element.dispatchEvent(new Event("input", { bubbles: true }))
  }

  // --- keys ------------------------------------------------------------------------

  handleKeyDown(event) {
    const renderer = this.activeRenderer()
    if (!renderer) return
    // The shell also refuses to act on a popup that is no longer connected, so a broadcast
    // refresh that swept the menu away can never insert whoever was highlighted into a menu
    // the user watched disappear.
    if (!renderer.onKeyDown({ event })) return

    // preventDefault is OURS to do. @tiptap/suggestion called it for the editor; on a plain
    // field nothing does, and the defaults are destructive: Enter submits the "Add an ask"
    // form (creating an ask literally named "Follow up with @ri"), or inserts a newline
    // mid-token in the textarea; Escape closes an enclosing <dialog> or the peek drawer,
    // taking the half-written note with it; the arrows walk the caret out of the query.
    event.preventDefault()
    event.stopPropagation()
    if (event.key === "Escape") this.dismiss()
  }

  handleKeyUp(event) {
    // Horizontal caret movement only. The shell never consumes these four (it takes
    // ArrowUp/ArrowDown/Enter/Tab/Escape), so refreshing on them is always correct — and it
    // must run even while the popup is open, or moving the caret out of the query leaves a
    // menu on screen that no longer describes what is being typed. ArrowUp/ArrowDown are
    // absent for the opposite reason: refreshing on those would reset the highlighted row to
    // 0 on every press, so the user could never walk down the list.
    if ([ "ArrowLeft", "ArrowRight", "Home", "End" ].includes(event.key)) this.refresh()
  }
}
